import React, { useEffect, useMemo, useRef, useState } from "react"
import api from "../../utils/api"
import GroupSearchResultCard from "./GroupSearchResultCard"
import GroupJoinRequestModal from "./GroupJoinRequestModal"
import { getApiErrorMessage } from "../../utils/errorMessage"

const SEARCH_RESULTS_PAGE_SIZE = 3

function GroupSearchPanel({ registeredName = "", onClose, embedded = false }) {
  const [query, setQuery] = useState("")
  const [results, setResults] = useState([])
  const [visibleCount, setVisibleCount] = useState(SEARCH_RESULTS_PAGE_SIZE)
  const [isSearching, setIsSearching] = useState(false)
  const [searchError, setSearchError] = useState("")
  const [searchDone, setSearchDone] = useState(false)
  const [selectedGroup, setSelectedGroup] = useState(null)
  const [isSubmittingJoin, setIsSubmittingJoin] = useState(false)
  const [joinSubmitError, setJoinSubmitError] = useState("")
  const [joinSuccess, setJoinSuccess] = useState("")
  const latestSearchRequestRef = useRef(0)

  const normalizedRegisteredName = useMemo(() => {
    const name = String(registeredName || "").trim()
    return name || "My Registered Name"
  }, [registeredName])

  useEffect(() => {
    const trimmedQuery = String(query || "").trim()
    setJoinSuccess("")
    setSearchError("")
    setVisibleCount(SEARCH_RESULTS_PAGE_SIZE)
    if (!trimmedQuery) {
      setResults([])
      setIsSearching(false)
      setSearchDone(false)
      return undefined
    }

    const requestId = latestSearchRequestRef.current + 1
    latestSearchRequestRef.current = requestId
    const timeoutId = setTimeout(async () => {
      setIsSearching(true)
      try {
        const response = await api.get("/groups/search", {
          params: { q: trimmedQuery },
        })
        if (latestSearchRequestRef.current !== requestId) return
        const groups = Array.isArray(response?.data?.groups) ? response.data.groups : []
        const normalizedQuery = trimmedQuery.toLowerCase()
        const filteredGroups = groups.filter((group) => {
          const groupCode = String(group?.groupCode || "").trim().toLowerCase()
          return groupCode === normalizedQuery
        })
        setResults(filteredGroups)
        setSearchDone(true)
      } catch (error) {
        if (latestSearchRequestRef.current !== requestId) return
        setResults([])
        setSearchDone(true)
        setSearchError(getApiErrorMessage(error, "Failed to search groups."))
      } finally {
        if (latestSearchRequestRef.current === requestId) {
          setIsSearching(false)
        }
      }
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [query])

  const handleOpenJoinModal = (group) => {
    setJoinSubmitError("")
    setSelectedGroup(group || null)
  }

  const handleCloseJoinModal = () => {
    if (isSubmittingJoin) return
    setSelectedGroup(null)
    setJoinSubmitError("")
  }

  const handleSubmitJoinRequest = async ({ preferredNameMode, akaName }) => {
    if (!selectedGroup?.id) return
    setIsSubmittingJoin(true)
    setJoinSubmitError("")
    setJoinSuccess("")
    try {
      const response = await api.post(`/groups/${selectedGroup.id}/join-requests`, {
        preferredNameMode,
        akaName,
      })

      if (response?.data?.joined) {
        setResults((prev) =>
          prev.filter((row) => String(row?.id || "") !== String(selectedGroup.id))
        )
        setJoinSuccess(
          response?.data?.message ||
            "You joined this group immediately because approval is not required."
        )
      } else {
        setResults((prev) =>
          prev.map((row) =>
            String(row?.id || "") === String(selectedGroup.id)
              ? {
                  ...row,
                  hasPendingJoinRequest: true,
                  hasPendingInvite: false,
                  pendingJoinRequest: {
                    id: "pending",
                    preferredDisplayName:
                      preferredNameMode === "aka"
                        ? String(akaName || "").trim()
                        : normalizedRegisteredName,
                    preferredNameMode,
                    createdAt: new Date().toISOString(),
                  },
                }
              : row
          )
        )
        setJoinSuccess(
          response?.data?.message ||
            "Join request submitted. Admin approval is required before you are added to the group."
        )
      }
      setSelectedGroup(null)
    } catch (error) {
      setJoinSubmitError(getApiErrorMessage(error, "Failed to submit join request."))
    } finally {
      setIsSubmittingJoin(false)
    }
  }

  const handleShowMore = () => {
    setVisibleCount((previous) => previous + SEARCH_RESULTS_PAGE_SIZE)
  }

  const visibleResults = useMemo(() => results.slice(0, visibleCount), [results, visibleCount])
  const hasMoreResults = results.length > visibleCount

  const noResults = searchDone && !isSearching && !searchError && results.length === 0

  const inner = (
    <>
      {!embedded && (
        <div className="flex items-center justify-between gap-2">
          <h3 className="text-sm font-semibold text-gray-900">Search Groups</h3>
          {onClose && (
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg px-2.5 py-1 text-xs font-semibold text-gray-500 hover:bg-gray-100 transition-colors"
            >
              Close
            </button>
          )}
        </div>
      )}
      {!embedded && (
        <p className="text-[11px] text-gray-500 mt-1">
          Search by full Group ID, then request to join (or join instantly if approval is off).
        </p>
      )}

      <div className={embedded ? "" : "mt-3"}>
        <input
          type="text"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          className="w-full px-3 py-2 rounded-lg border border-gray-300 text-xs text-gray-800 focus:outline-none focus:ring-2 focus:ring-sky-200"
        />
      </div>

      {!!joinSuccess && (
        <div className="mt-3 border border-emerald-300 bg-emerald-50 text-emerald-700 text-xs p-2 rounded-lg">
          {joinSuccess}
        </div>
      )}
      {!!searchError && (
        <div className="mt-3 border border-red-300 bg-red-50 text-red-700 text-xs p-2 rounded-lg">
          {searchError}
        </div>
      )}

      {isSearching && (
        <p className="mt-3 text-xs text-gray-500">Searching groups...</p>
      )}

      {noResults && (
        <p className="mt-3 text-xs text-gray-500">No groups found for your search.</p>
      )}

      {results.length > 0 && (
        <div className="mt-3 space-y-2">
          {visibleResults.map((group) => (
            <GroupSearchResultCard
              key={group.id}
              group={group}
              onJoin={handleOpenJoinModal}
            />
          ))}
          {hasMoreResults && (
            <button
              type="button"
              onClick={handleShowMore}
              className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-semibold text-gray-700 transition-colors hover:bg-gray-50"
            >
              Show more
            </button>
          )}
        </div>
      )}

      <GroupJoinRequestModal
        open={!!selectedGroup}
        group={selectedGroup}
        registeredName={normalizedRegisteredName}
        isSubmitting={isSubmittingJoin}
        submitError={joinSubmitError}
        onClose={handleCloseJoinModal}
        onSubmit={handleSubmitJoinRequest}
      />
    </>
  )

  if (embedded) return inner

  return (
    <div className="bg-white/95 rounded-2xl border border-gray-200 shadow-sm p-4">
      {inner}
    </div>
  )
}

export default GroupSearchPanel
